import { Truck, Clock, MapPin, ShieldCheck, Info } from 'lucide-react';
import SEO from '../components/SEO';

export default function ShippingPage() {
    return (
        <div className="min-h-screen bg-white">
            <SEO title="Shipping Policy" description="NSO India Shipping and Delivery Policy" noIndex={true} />
            {/* Hero Section */}
            <section className="bg-surface py-16 md:py-24">
                <div className="max-w-7xl mx-auto px-4 md:px-8">
                    <div className="max-w-3xl">
                        <span className="text-sm uppercase tracking-widest text-primary font-body font-semibold mb-4 block">
                            Logistics & Delivery
                        </span>
                        <h1 className="font-heading text-4xl md:text-6xl tracking-tight text-secondary mb-6">
                            Shipping <span className="text-primary">Info</span>
                        </h1>
                        <p className="text-lg text-muted-foreground font-body leading-relaxed">
                            We strive to deliver your products in the fastest time possible. Learn more about our shipping partners, timelines, and costs.
                        </p>
                    </div>
                </div>
            </section>

            <section className="py-16 md:py-24">
                <div className="max-w-7xl mx-auto px-4 md:px-8">
                    <div className="grid lg:grid-cols-3 gap-8 mb-16">
                        <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-soft">
                            <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mb-6">
                                <Truck className="w-8 h-8 text-primary" />
                            </div>
                            <h3 className="font-heading text-xl text-secondary mb-4">Standard Delivery</h3>
                            <p className="text-muted-foreground font-body leading-relaxed">
                                Free for orders above ₹999. Enjoy reliable door-to-door delivery across India within 3-7 business days.
                            </p>
                        </div>
                        <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-soft">
                            <div className="w-14 h-14 bg-success/10 rounded-2xl flex items-center justify-center mb-6">
                                <Clock className="w-8 h-8 text-success" />
                            </div>
                            <h3 className="font-heading text-xl text-secondary mb-4">Processing Time</h3>
                            <p className="text-muted-foreground font-body leading-relaxed">
                                Most orders are dispatched within 24 hours. We work 6 days a week to ensure your package leaves our warehouse quickly.
                            </p>
                        </div>
                        <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-soft">
                            <div className="w-14 h-14 bg-warning/10 rounded-2xl flex items-center justify-center mb-6">
                                <ShieldCheck className="w-8 h-8 text-warning" />
                            </div>
                            <h3 className="font-heading text-xl text-secondary mb-4">Secure Packaging</h3>
                            <p className="text-muted-foreground font-body leading-relaxed">
                                Every product is multiple-checked and transit-safe packed. Smartphones and luxury eyewear are triple-bubble wrapped.
                            </p>
                        </div>
                    </div>

                    <div className="space-y-12 max-w-4xl">
                        <div className="animate-fade-in">
                            <h2 className="font-heading text-3xl text-secondary mb-6">Delivery Timelines</h2>
                            <div className="bg-surface rounded-3xl p-8 overflow-hidden">
                                <table className="w-full text-left font-body">
                                    <thead>
                                        <tr className="border-b border-gray-200">
                                            <th className="py-4 font-semibold text-secondary">Region</th>
                                            <th className="py-4 font-semibold text-secondary">Timeframe</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        <tr>
                                            <td className="py-4 text-muted-foreground">Metros (Mumbai, Delhi, etc.)</td>
                                            <td className="py-4 text-secondary font-medium">2 - 3 Business Days</td>
                                        </tr>
                                        <tr>
                                            <td className="py-4 text-muted-foreground">South/North India Cities</td>
                                            <td className="py-4 text-secondary font-medium">3 - 5 Business Days</td>
                                        </tr>
                                        <tr>
                                            <td className="py-4 text-muted-foreground">Northeast & Remote Areas</td>
                                            <td className="py-4 text-secondary font-medium">7 - 10 Business Days</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="animate-fade-in">
                            <h2 className="font-heading text-3xl text-secondary mb-6">Tracking Your Order</h2>
                            <p className="text-lg text-muted-foreground font-body leading-relaxed mb-6">
                                Once your order is shipped, you will receive an SMS and Email with a tracking link. You can also track your order directly on our website using your Order ID.
                            </p>
                            <div className="bg-primary/5 border-l-4 border-primary p-6 rounded-r-3xl flex gap-4">
                                <Info className="w-6 h-6 text-primary flex-shrink-0" />
                                <p className="text-secondary font-body italic">
                                    Note: Tracking details might take up to 12 hours to update after the package is picked up by the courier partner.
                                </p>
                            </div>
                        </div>

                        <div className="animate-fade-in">
                            <h2 className="font-heading text-3xl text-secondary mb-6 flex items-center gap-3">
                                <MapPin className="w-8 h-8 text-primary" />
                                Non-Serviceable Areas
                            </h2>
                            <p className="text-lg text-muted-foreground font-body leading-relaxed">
                                While we deliver to 19,000+ pin codes in India, certain remote areas or restricted containment zones may not be serviceable. If your zip code is not serviceable, we will contact you within 24 hours of order placement.
                            </p>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
